import React, { useEffect, useMemo, useState } from 'react';
import { FileBadge, RefreshCw, UploadCloud, X } from 'lucide-react';
import { Modal } from '@components/Dialog';
import { Button } from '@components/Button';
import { DateInputBR } from '@components/DateInputBR';
import { FileTypeIcon } from '@components/FileTypeIcon';
import cacDocumentService from '@services/cacDocumentService';
import documentService, { CAC_FOLDER_NAME, findFolderByName } from '@services/documentService';
import { DocumentUploadModal } from '@pages/documents/DocumentUploadModal';
import type { Document, DocumentFolder } from '@/types/document';
import {
  CAC_DOC_TYPES,
  CAC_DOC_TYPE_LABELS,
  firearmLabel,
  requiresFirearm,
  type CacDocType,
  type CacDocument,
  type CacDocumentInput,
  type Firearm,
} from '@/types/cac';
import { formatBytes } from '@/utils/formatters';

export type CacFormMode = 'create' | 'edit' | 'renew';

interface Props {
  mode: CacFormMode;
  /** Documento sendo editado (edit) ou renovado (renew). */
  source?: CacDocument | null;
  firearms: Firearm[];
  /** Arma pré-selecionada (ficha da arma). */
  prefillFirearmId?: number;
  onClose: () => void;
  onSaved: (doc: CacDocument) => void;
  onError: (err: unknown) => void;
}

interface FormState {
  doc_type: CacDocType;
  firearm_id: number | '';
  number: string;
  issuer_unit: string;
  issued_at: string;
  expires_at: string;
  notes: string;
}

type FileChoice = 'none' | 'existing' | 'upload';

const labelCls = 'text-label block mb-1';

/**
 * Cadastro / edição / renovação de documento CAC. CRAF e GT exigem arma; CR, porte e outro não têm.
 * Arquivo: nenhum, um Document já enviado (pasta "Armas (CAC)") ou um novo (upload com
 * `ref_type = cac_document`). Renovar: POST /cac-documents/:id/renew com nova validade obrigatória.
 */
export const CacDocumentFormModal: React.FC<Props> = ({ mode, source, firearms, prefillFirearmId, onClose, onSaved, onError }) => {
  const [form, setForm] = useState<FormState>(() => ({
    doc_type: source?.doc_type ?? (prefillFirearmId ? 'craf' : 'cr'),
    firearm_id: source?.firearm_id ?? prefillFirearmId ?? '',
    number: mode === 'renew' ? '' : source?.number ?? '',
    issuer_unit: source?.issuer_unit ?? '',
    issued_at: mode === 'renew' ? '' : source?.issued_at ?? '',
    expires_at: mode === 'renew' ? '' : source?.expires_at ?? '',
    notes: mode === 'renew' ? '' : source?.notes ?? '',
  }));
  const [fileChoice, setFileChoice] = useState<FileChoice>(mode !== 'renew' && source?.document_id ? 'existing' : 'none');
  const [selectedDoc, setSelectedDoc] = useState<Document | null>(mode !== 'renew' ? source?.document ?? null : null);
  const [existingDocs, setExistingDocs] = useState<Document[]>([]);
  const [folders, setFolders] = useState<DocumentFolder[]>([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const cacFolder = useMemo(() => findFolderByName(folders, CAC_FOLDER_NAME), [folders]);

  useEffect(() => {
    documentService.getFolders().then(setFolders).catch(onError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (fileChoice !== 'existing') return;
    documentService
      .list({ folder_id: cacFolder?.id, page_size: 100 })
      .then((r) => setExistingDocs(r.data))
      .catch(onError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileChoice, cacFolder?.id]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));

  const needsFirearm = requiresFirearm(form.doc_type);
  const activeFirearms = firearms.filter((f) => f.status === 'active' || f.id === form.firearm_id);
  const dateError = form.issued_at && form.expires_at && form.expires_at < form.issued_at ? 'A validade não pode ser antes da emissão.' : null;
  const canSubmit = !saving && !!form.expires_at && (!needsFirearm || !!form.firearm_id) && !dateError && (fileChoice !== 'existing' || !!selectedDoc);

  const title = mode === 'create' ? 'Novo documento CAC' : mode === 'edit' ? 'Editar documento CAC' : `Renovar ${CAC_DOC_TYPE_LABELS[form.doc_type]}`;

  const save = async () => {
    if (!canSubmit) return;
    const payload: CacDocumentInput = {
      doc_type: form.doc_type,
      firearm_id: needsFirearm ? Number(form.firearm_id) : null,
      number: form.number.trim() || null,
      issuer_unit: form.issuer_unit.trim() || null,
      issued_at: form.issued_at || null,
      expires_at: form.expires_at,
      document_id: fileChoice === 'none' ? null : selectedDoc?.id ?? null,
      notes: form.notes.trim() || null,
    };
    setSaving(true);
    try {
      let saved: CacDocument;
      if (mode === 'edit' && source) saved = await cacDocumentService.update(source.id, payload);
      else if (mode === 'renew' && source) saved = await cacDocumentService.renew(source.id, payload);
      else saved = await cacDocumentService.create(payload);
      // Arquivo novo: grava o vínculo com o documento CAC recém-criado (melhor esforço).
      if (selectedDoc && payload.document_id && selectedDoc.ref_type === 'cac_document' && selectedDoc.ref_id !== saved.id) {
        await documentService
          .update(selectedDoc.id, {
            title: selectedDoc.title,
            description: selectedDoc.description ?? null,
            folder_id: selectedDoc.folder_id,
            doc_date: selectedDoc.doc_date ?? null,
            expires_at: selectedDoc.expires_at ?? null,
            remind_days: selectedDoc.remind_days,
            tags: selectedDoc.tags ?? null,
            ref_type: 'cac_document',
            ref_id: saved.id,
          })
          .catch(() => undefined);
      }
      onSaved(saved);
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  };

  const fileSummary = selectedDoc && (
    <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-2">
      <FileTypeIcon fileName={selectedDoc.file_name} contentType={selectedDoc.content_type} size={16} />
      <div className="min-w-0 flex-1">
        <p className="text-sm text-slate-800 truncate">{selectedDoc.title}</p>
        <p className="text-small truncate">
          {selectedDoc.file_name} · {formatBytes(selectedDoc.size_bytes)}
        </p>
      </div>
      <button
        type="button"
        className="p-1 text-slate-400 hover:text-red-600"
        title="Desvincular arquivo"
        onClick={() => {
          setSelectedDoc(null);
          setFileChoice('none');
        }}
      >
        <X size={14} />
      </button>
    </div>
  );

  return (
    <>
      <Modal isOpen onClose={() => !uploadOpen && onClose()} size="lg" closeOnBackdrop={!saving && !uploadOpen}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
          {mode === 'renew' ? <RefreshCw size={18} className="text-slate-500" /> : <FileBadge size={18} className="text-slate-500" />}
          <h2 className="text-h3">{title}</h2>
        </div>
        <div className="px-6 py-4 max-h-[70vh] overflow-y-auto custom-scrollbar grid grid-cols-1 sm:grid-cols-2 gap-3">
          {mode === 'renew' && source && (
            <p className="sm:col-span-2 text-small rounded-lg bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2">
              O documento atual ({source.number ? `nº ${source.number}, ` : ''}validade até {source.expires_at.split('-').reverse().join('/')}) será marcado
              como renovado e deixará de gerar alertas. Informe os dados do novo.
            </p>
          )}
          <div>
            <label className={labelCls}>
              Tipo <span className="text-red-500">*</span>
            </label>
            <select
              className="select-field"
              value={form.doc_type}
              disabled={mode === 'renew'}
              onChange={(e) => {
                const t = e.target.value as CacDocType;
                setForm((f) => ({ ...f, doc_type: t, firearm_id: requiresFirearm(t) ? f.firearm_id : '' }));
              }}
            >
              {CAC_DOC_TYPES.map((t) => (
                <option key={t} value={t}>
                  {CAC_DOC_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </div>
          {needsFirearm && (
            <div>
              <label className={labelCls}>
                Arma <span className="text-red-500">*</span>
              </label>
              <select
                className="select-field"
                value={form.firearm_id}
                disabled={mode === 'renew' || !!prefillFirearmId}
                onChange={(e) => set('firearm_id', e.target.value ? Number(e.target.value) : '')}
              >
                <option value="">Selecione…</option>
                {activeFirearms.map((f) => (
                  <option key={f.id} value={f.id}>
                    {firearmLabel(f)}
                  </option>
                ))}
              </select>
              {firearms.length === 0 && <p className="text-small mt-1">Cadastre a arma em Acervo CAC → Armas primeiro.</p>}
            </div>
          )}
          <div>
            <label className={labelCls}>Número</label>
            <input className="input-field" maxLength={100} value={form.number} onChange={(e) => set('number', e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Órgão emissor</label>
            <input className="input-field" maxLength={200} value={form.issuer_unit} placeholder="Ex.: SFPC/2ª RM" onChange={(e) => set('issuer_unit', e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Emissão</label>
            <DateInputBR className="input-field w-full" value={form.issued_at} onChange={(v) => set('issued_at', v)} />
          </div>
          <div>
            <label className={labelCls}>
              Validade <span className="text-red-500">*</span>
            </label>
            <DateInputBR className="input-field w-full" value={form.expires_at} min={form.issued_at || undefined} onChange={(v) => set('expires_at', v)} />
          </div>

          <div className="sm:col-span-2 border-t border-slate-100 pt-3 space-y-2">
            <label className={labelCls}>Arquivo</label>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-700">
              {(
                [
                  ['none', 'Nenhum'],
                  ['existing', 'Escolher um já enviado'],
                  ['upload', 'Enviar novo'],
                ] as [FileChoice, string][]
              ).map(([v, label]) => (
                <label key={v} className="inline-flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="file-choice"
                    className="checkbox-field rounded-full"
                    checked={fileChoice === v}
                    onChange={() => {
                      setFileChoice(v);
                      if (v === 'none') setSelectedDoc(null);
                      if (v === 'upload') setUploadOpen(true);
                    }}
                  />
                  {label}
                </label>
              ))}
            </div>
            {fileChoice === 'existing' && (
              <select
                className="select-field"
                value={selectedDoc?.id ?? ''}
                onChange={(e) => setSelectedDoc(existingDocs.find((d) => d.id === Number(e.target.value)) ?? null)}
              >
                <option value="">Selecione um arquivo da pasta "{cacFolder?.name ?? CAC_FOLDER_NAME}"…</option>
                {existingDocs.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.title} ({d.file_name})
                  </option>
                ))}
              </select>
            )}
            {fileChoice === 'upload' && !selectedDoc && (
              <Button variant="outline" size="sm" icon={<UploadCloud />} onClick={() => setUploadOpen(true)}>
                Escolher arquivo
              </Button>
            )}
            {fileSummary}
          </div>

          <div className="sm:col-span-2">
            <label className={labelCls}>Observações</label>
            <textarea className="textarea-field" rows={2} maxLength={2000} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
          </div>
          {dateError && <p className="sm:col-span-2 text-sm text-red-600">{dateError}</p>}
        </div>
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-lg flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={save} loading={saving} disabled={!canSubmit}>
            {mode === 'renew' ? 'Renovar' : mode === 'edit' ? 'Salvar' : 'Cadastrar'}
          </Button>
        </div>
      </Modal>

      {uploadOpen && (
        <DocumentUploadModal
          folders={folders}
          single
          prefill={{ folder_id: cacFolder?.id, lockFolder: !!cacFolder, ref_type: 'cac_document', ref_id: mode === 'edit' ? source?.id : undefined }}
          onClose={() => {
            setUploadOpen(false);
            if (!selectedDoc) setFileChoice('none');
          }}
          onUploaded={(docs) => {
            setUploadOpen(false);
            if (docs[0]) setSelectedDoc(docs[0]);
            else setFileChoice('none');
          }}
          onError={onError}
        />
      )}
    </>
  );
};
